import React, { useRef, useState } from 'react';
import { PROJECT_MEDIA_FILE_ACCEPT, uploadProjectMediaAsset, type ProjectMediaAssetKind, type UploadResult } from '../../features/files/utils/storage-service.ts';
import type { ProjectScope } from '../../features/sync/project-scope.ts';
import { logger } from '../../utils/logger.ts';
import { showToast } from '../../utils/notifications.ts';
import { cn } from '../../utils/cn.ts';
import { Button } from './Button.tsx';
import { Icon } from './Icon.tsx';

const NativeInput = 'input';

interface ProjectMediaUploaderProps {
    scope: ProjectScope;
    kind: ProjectMediaAssetKind;
    currentUrl: string | null | undefined;
    onUrlChange: (url: string | null, result?: UploadResult) => void;
    label: string;
    className?: string;
    readOnly?: boolean;
}

export const ProjectMediaUploader: React.FC<ProjectMediaUploaderProps> = ({
    scope,
    kind,
    currentUrl,
    onUrlChange,
    label,
    className,
    readOnly = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setIsUploading(true);
        setUploadProgress(0);
        try {
            const result = await uploadProjectMediaAsset(file, scope, kind, (progress) => {
                setUploadProgress(Math.round(progress.percent));
            });
            if (kind === 'production-logo' && !result.url) {
                throw new Error('Project media upload did not return a download URL.');
            }
            if (kind === 'profile-headshot' && !result.storagePath) {
                throw new Error('Project media upload did not return a storage path.');
            }
            onUrlChange(result.url ?? null, result);
            showToast(`${label} uploaded successfully`, 'success');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Upload failed';
            logger.error('Project media upload error:', error);
            showToast(message, 'error');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    if (readOnly) {
        return currentUrl ? (
            <img src={currentUrl} alt={label} width={200} height={100} className={cn('max-h-[100px] max-w-[200px] object-contain', className)} />
        ) : null;
    }

    return (
        <div className={cn('flex flex-wrap items-center gap-3', className)}>
            <NativeInput
                ref={fileInputRef}
                type="file"
                accept={PROJECT_MEDIA_FILE_ACCEPT}
                className="hidden"
                onChange={(event) => { void handleFileChange(event); }}
            />

            {currentUrl ? (
                <div className="flex items-center gap-3">
                    <img
                        src={currentUrl}
                        alt={label}
                        width={144}
                        height={72}
                        className="max-h-[72px] max-w-[144px] rounded-md border border-gray-200 object-contain dark:border-border-dark"
                    />
                    <Button
                        aria-label={`Remove ${label}`}
                        disabled={isUploading}
                        leftIcon="close"
                        onClick={() => onUrlChange(null)}
                        size="sm"
                        variant="danger-subtle"
                    >
                        Remove
                    </Button>
                </div>
            ) : null}

            <Button
                disabled={isUploading}
                isLoading={isUploading}
                leftIcon="image"
                onClick={() => fileInputRef.current?.click()}
                variant="secondary"
            >
                {isUploading ? `${uploadProgress}%` : label}
            </Button>
            {isUploading ? (
                <span className="inline-flex items-center gap-1 text-gray-500 text-sm dark:text-gray-400" aria-live="polite">
                    <Icon name="cloud_upload" size="sm" />
                    Uploading
                </span>
            ) : null}
        </div>
    );
};
